import { RBox } from '../../render/parts'
import { Sofa } from '../parts/seating'
import type { AssetContext, AssetDefinition } from '../types'

/**
 * The footprint is the WIDEST variant. The two-seater is simply centred inside
 * the same 2.4 x 0.9 cell, so neither variant ever overhangs its own footprint
 * and rotation/snapping stays predictable.
 */
const WIDTH = 2.4
const DEPTH = 0.9

/**
 * Top of the reclined back cushion: centred at y = 0.62, half-height 0.21,
 * tilted 0.08 rad.
 */
const HEIGHT = 0.84

interface SofaSize {
  width: number
  seats: number
}

const DEFAULT_SIZE: SofaSize = { width: 1.8, seats: 2 }

const SIZES: Record<string, SofaSize> = {
  dos: DEFAULT_SIZE,
  tres: { width: 2.4, seats: 3 },
}

/**
 * Throw cushion propped in the corner between the seat pad and the back rest.
 * It overlaps both by ~1 cm on purpose — coplanar faces z-fight badly under the
 * white-surface / hairline-contour treatment.
 */
function ThrowCushion({ x, accent, flip }: { x: number; accent?: string; flip: number }) {
  return (
    <RBox
      tone="accent"
      color={accent}
      size={[0.34, 0.28, 0.11]}
      radius={0.035}
      position={[x, 0.67, -0.14]}
      rotation={[-0.3, flip * 0.16, 0]}
    />
  )
}

/**
 * Soft sofa for breakout and reception areas. Body, seat pads and arms come
 * from the shared `Sofa` sub-assembly; the two accent throw cushions are the
 * single colour note.
 */
function SofaItem({ accent, variant = 'dos' }: AssetContext) {
  const { width, seats } = SIZES[variant] ?? DEFAULT_SIZE

  return (
    <group>
      <Sofa width={width} seats={seats} />
      {[-1, 1].map((side) => (
        <ThrowCushion key={side} x={side * (width / 2 - 0.29)} flip={side} accent={accent} />
      ))}
    </group>
  )
}

export const definition: AssetDefinition = {
  id: 'sofa',
  name: 'Sofá',
  category: 'seating',
  footprint: [WIDTH, DEPTH],
  height: HEIGHT,
  seats: 2,
  occupantSide: 'front',
  seatInset: 0.45,
  accentable: true,
  variants: [
    { id: 'dos', name: 'Dos plazas', footprint: [DEFAULT_SIZE.width, DEPTH] },
    { id: 'tres', name: 'Tres plazas', footprint: [WIDTH, DEPTH] },
  ],
  keywords: ['sofá', 'sillón doble', 'sala', 'lounge', 'sofa', 'couch', 'settee', 'recepción'],
  Component: SofaItem,
}
